// The "juice" layer: a tiny ticker-driven tween engine, easing curves, a
// particle burst, and screen-shake. Deliberately dependency-free so the feel
// is fully in our hands.
import { Container, Graphics, type Ticker } from "pixi.js";

export type EaseFn = (t: number) => number;

export const Ease = {
  linear: (t: number) => t,
  outQuad: (t: number) => 1 - (1 - t) * (1 - t),
  inOutQuad: (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2),
  outCubic: (t: number) => 1 - Math.pow(1 - t, 3),
  inOutCubic: (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
  outBack: (t: number) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  },
  outElastic: (t: number) => {
    const c4 = (2 * Math.PI) / 3;
    return t === 0 ? 0 : t === 1 ? 1 : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
  },
} satisfies Record<string, EaseFn>;

interface Anim {
  elapsed: number;
  duration: number;
  ease: EaseFn;
  onUpdate: (v: number) => void;
  onComplete?: () => void;
  dead: boolean;
  owner?: unknown;
}

const anims: Anim[] = [];

/** Advance every live tween by `dt` ms. Exposed so tests can drive time. */
export function stepJuice(dt: number) {
  for (const a of anims) {
    if (a.dead) continue;
    a.elapsed += dt;
    if (a.elapsed < 0) continue; // still inside `delay`
    const p = a.duration <= 0 ? 1 : Math.min(1, a.elapsed / a.duration);
    a.onUpdate(a.ease(p));
    if (p >= 1) {
      a.dead = true;
      a.onComplete?.();
    }
  }
  for (let i = anims.length - 1; i >= 0; i--) if (anims[i].dead) anims.splice(i, 1);
}

/** Drive all tweens off a single ticker. Call once at startup. */
export function initJuice(ticker: Ticker) {
  ticker.add((tk) => stepJuice(tk.deltaMS));
}

export function tween(opts: {
  duration: number;
  onUpdate: (v: number) => void;
  ease?: EaseFn;
  delay?: number;
  onComplete?: () => void;
  owner?: unknown;
}): Anim {
  const a: Anim = {
    elapsed: -(opts.delay ?? 0),
    duration: opts.duration,
    ease: opts.ease ?? Ease.outCubic,
    onUpdate: opts.onUpdate,
    onComplete: opts.onComplete,
    dead: false,
    owner: opts.owner,
  };
  anims.push(a);
  return a;
}

/** Abort in-flight tweens owned by `owner` (their onComplete does NOT fire). */
export function cancelMotion(owner: unknown) {
  for (const a of anims) if (a.owner === owner) a.dead = true;
}

/** Fire `fn` after `ms`, scheduled on the same ticker (pauses with the game). */
export function delay(ms: number, fn: () => void) {
  return tween({ duration: ms, ease: Ease.linear, onUpdate: () => {}, onComplete: fn });
}

/** tween() bound to a display object: inert if `o` is already destroyed, and a
 *  no-op (no update, no onComplete) if `o` is destroyed mid-flight — pixi v8
 *  throws on mutating a destroyed Container, and a throw inside the shared
 *  ticker callback would stall every animation in the game. */
function tweenFor(
  o: Container,
  opts: { duration: number; ease: EaseFn; onUpdate: (v: number) => void; onComplete?: () => void },
): Anim {
  if (o.destroyed) return { elapsed: 0, duration: 0, ease: Ease.linear, onUpdate: () => {}, dead: true };
  return tween({
    duration: opts.duration,
    ease: opts.ease,
    owner: o,
    onUpdate: (v) => {
      if (!o.destroyed) opts.onUpdate(v);
    },
    onComplete: opts.onComplete && (() => !o.destroyed && opts.onComplete!()),
  });
}

export function moveTo(o: Container, x: number, y: number, duration: number, ease: EaseFn = Ease.outCubic, onComplete?: () => void) {
  if (o.destroyed) return tweenFor(o, { duration, ease, onUpdate: () => {} });
  const sx = o.x;
  const sy = o.y;
  return tweenFor(o, { duration, ease, onComplete, onUpdate: (v) => {
    o.x = sx + (x - sx) * v;
    o.y = sy + (y - sy) * v;
  } });
}

export function scaleTo(o: Container, s: number, duration: number, ease: EaseFn = Ease.outBack, onComplete?: () => void) {
  if (o.destroyed) return tweenFor(o, { duration, ease, onUpdate: () => {} });
  const ss = o.scale.x;
  return tweenFor(o, { duration, ease, onComplete, onUpdate: (v) => o.scale.set(ss + (s - ss) * v) });
}

export function rotateTo(o: Container, r: number, duration: number, ease: EaseFn = Ease.outCubic, onComplete?: () => void) {
  if (o.destroyed) return tweenFor(o, { duration, ease, onUpdate: () => {} });
  const sr = o.rotation;
  return tweenFor(o, { duration, ease, onComplete, onUpdate: (v) => { o.rotation = sr + (r - sr) * v; } });
}

export function fadeTo(o: Container, a: number, duration: number, ease: EaseFn = Ease.linear, onComplete?: () => void) {
  if (o.destroyed) return tweenFor(o, { duration, ease, onUpdate: () => {} });
  const sa = o.alpha;
  return tweenFor(o, { duration, ease, onComplete, onUpdate: (v) => { o.alpha = sa + (a - sa) * v; } });
}

/** Damped random shake. Assumes `o` rests at the origin (use a dedicated layer). */
export function shake(o: Container, intensity: number, duration: number) {
  tween({
    duration,
    ease: Ease.linear,
    onUpdate: (v) => {
      const d = (1 - v) * intensity;
      o.x = (Math.random() * 2 - 1) * d;
      o.y = (Math.random() * 2 - 1) * d;
    },
    onComplete: () => { o.x = 0; o.y = 0; },
  });
}

/** Confetti/spark burst at a point. Particles self-destruct. */
export function burst(layer: Container, x: number, y: number, colors: number[], count = 28) {
  for (let i = 0; i < count; i++) {
    const p = new Graphics();
    const color = colors[(Math.random() * colors.length) | 0];
    const size = 4 + Math.random() * 6;
    if (Math.random() < 0.5) p.rect(-size / 2, -size / 2, size, size * 1.6).fill(color);
    else p.circle(0, 0, size * 0.6).fill(color);
    p.position.set(x, y);
    p.rotation = Math.random() * Math.PI;
    layer.addChild(p);

    const ang = Math.random() * Math.PI * 2;
    const dist = 50 + Math.random() * 150;
    const tx = x + Math.cos(ang) * dist;
    const ty = y + Math.sin(ang) * dist;
    const drop = 60 + Math.random() * 90; // gravity arc
    const spin = (Math.random() * 2 - 1) * 9;
    const r0 = p.rotation;

    tween({
      duration: 520 + Math.random() * 480,
      ease: Ease.outCubic,
      onUpdate: (v) => {
        p.x = x + (tx - x) * v;
        p.y = y + (ty - y) * v + drop * v * v;
        p.rotation = r0 + spin * v;
        p.alpha = 1 - v * v;
        p.scale.set(1 - v * 0.4);
      },
      onComplete: () => p.destroy(),
    });
  }
}
